import React from 'react';
import { OwcTable, OwcTableCell, OwcTableHeader, OwcTableHeaderCell, OwcTableBody, OwcTableRow,
         OwcTabs, OwcTab, OwcTypography} from '@one/react';
import '../../styles.scss';

import {configData} from "../../config.js";
import {formatDate, fetchSigned, convertCamelCaseToTitleCase} from "../../shared/Utilities.js"
import { renderToString } from 'react-dom/server'

/**
 * Renders the device individual data source histories
 *
 * @copyright Roche 2025
 * @author Nick Draper
 */
class DeviceDataSources extends React.Component {
  INSTALLED_BASE_DATA_SOURCE = "installed base";
  COLUMN_RENAME_DICT = {"instrumentLocationAccountNo": "Account No"};
  /**
   * Constructor 
   * 
   * @param props The properties passed
   */
  constructor(props) {
    super(props);
    this.state = {
      historyPerSourceLoaded: false,
      historyPerSource: {},
      sourceList: [],
      selectedDataSource: null,
    };
  }

  componentDidMount() {
    if (this.props.deviceId !== null) {
      this.loadDeviceDataSources(this.props.deviceId);
    }
  }

  /** Runs whenever the properties of the control are changed
   * @param prevProps The previous properties dictionary
   * @param prevState The previous state dictionary
   */
  componentDidUpdate(prevProps, prevState) {
    if (prevProps.deviceId !== this.props.deviceId) {
      if (this.props.deviceId !== null) {
        this.loadDeviceDataSources(this.props.deviceId);
      } else {
        this.setState({
          historyPerSourceLoaded: false,
          historyPerSource: {},
          sourceList: [],
          selectedDataSource: null,
        });
      }
      
    }
  }

  
  /**
   * Loads the history data to display for a device
   * @param {*} deviceId the device_id to load
   */
  loadDeviceDataSources(deviceId) {
    console.log("loading device history-per-source for ", deviceId);
    this.setState({ historyPerSourceLoaded: false,
                    selectedDataSource: null,
    });

    fetchSigned(configData.DEVICE_DETAILS_API_URL + "history-per-source/" + deviceId + "/")
    .then(res => res.json())
    .then(
      (result) => {
        console.log("api result");
        console.log(result);
        const keys = Object.keys(result);
        if (this.INSTALLED_BASE_DATA_SOURCE in result) {
          const ibHistory = result[this.INSTALLED_BASE_DATA_SOURCE];
          if (ibHistory.length > 0) {
            ibHistory.forEach(row=> {
              row.instrumentLocationAccountNo = this.getAccountsText(row);
              if ("shipToAccountNo" in row) delete row["shipToAccountNo"];
              if ("soldToAccountNo" in row) delete row["soldToAccountNo"];
              if ("billToAccountNo" in row) delete row["billToAccountNo"];
              row.materialName = this.getMaterialNameAndNo(row);
              if ("materialNo" in row) delete row["materialNo"];
            });
          }
        }
        this.setState({ historyPerSource: result, 
          selectedDataSource: keys[0],
          sourceList: keys,
          historyPerSourceLoaded: true});
      },
      // Note: it's important to handle errors here
      // instead of a catch() block so that we don't swallow
      // exceptions from actual bugs in components.
      (error) => {
        this.setState({
          error
        });
      }
    )
  }


  /**
   * Generates and returns the text to be used to describe the accounts related to this device
   */
  getAccountsText(item) {
    const messageList = [];
    if (item.shipToAccountNo !== null) {
      messageList.push("shipTo: " + item.shipToAccountNo);
    }
    if (item.soldToAccountNo !== null) {
      messageList.push("soldTo: " + item.soldToAccountNo);
    }
    if (item.billToAccountNo !== null) {
      messageList.push("billTo: " + item.billToAccountNo);
    }
    if (item.instrumentLocationAccountNo !== null) {
      messageList.push("Location: " + item.instrumentLocationAccountNo);
    }
    if (messageList.length > 0){
      return(
        <span>
          {messageList.map((message, index) =>(<pre  key={item.deviceId + index} style={{marginTop:"0",marginBottom:"0"}}>{message}</pre>))}
        </span>);
    } else {
      return(
        <span style={{overflowWrap:"normal"}}>
          None
        </span>
      );
    }

  }

  /**
   * Generates and returns the text to be used to describe the material data to this device
   */
  getMaterialNameAndNo(item) {
    return(
      <div>
        <OwcTypography>{item.materialName}</OwcTypography>
        <br/>
        <OwcTypography variant="caption">{parseInt(item.materialNo)}</OwcTypography>
      </div>
    );
  }

  /**
   * Handles changes between the tabs of the different data sources
   * @param {*} tabName The selected source name
   */
  handleTabChange(tabName) {
    if (tabName !== this.state.selectedDataSource)
    {
      console.log(tabName);
      this.setState({ 
        selectedDataSource: tabName,
      });
    }
  }

  /**
   * Detemines the background colour for a row
   */
  applyColour(row, previousRow, fieldKey, styleDict={}) {
    if ((previousRow !== null) &&
        (renderToString(row[fieldKey]) !== renderToString(previousRow[fieldKey]))) {
          styleDict.backgroundColor = configData.COLOUR_YELLOW;
    }
    return styleDict;
  }

  camelCaseToSentenceCase (camelCaseString) {
    let result = camelCaseString;
    //convert camelCase to Sentence Case
    result = result.replace(/([A-Z])/g, " $1");
    result = result.charAt(0).toUpperCase() + result.slice(1);
    return result;
  }

  /**
   * Render the section displaying the device identity
   */
  renderHistoryRow(row, index) {
    if (this.state.historyPerSource[this.state.selectedDataSource] === undefined) return;
    
    const keyEnding = this.state.selectedDataSource + row.changeDate;
    let prevRow = null;
    const prevIndex = index + 1;
    if (prevIndex > 0 && prevIndex < this.state.historyPerSource[this.state.selectedDataSource].length) {
      prevRow = this.state.historyPerSource[this.state.selectedDataSource][prevIndex];
    }

    return (
      <OwcTableRow key={"HistoryRow" + keyEnding + index}>
        {
          Object.keys(row).map((columnName,colIndex) => (
            <OwcTableCell key={columnName+index+"Cell"+ keyEnding + colIndex}  valign="middle"
              style={this.applyColour(row, prevRow, columnName, {wordBreak:"normal"})}>
                {columnName.endsWith("Date")? formatDate(row[columnName]) : row[columnName]}
            </OwcTableCell>
          ))
        }
      </OwcTableRow>
    );
  }

  renderSourceTab(dataSourceName, index) {
    return (
      <OwcTab value={dataSourceName} key={"datasourceTab" + index}>{dataSourceName}</OwcTab>
    );
  }

  /**
   * Renders the controls
   * @returns The JSX of the controls
   */
  render() {
    let history = [];
    if (this.state.selectedDataSource in this.state.historyPerSource) {
      history = this.state.historyPerSource[this.state.selectedDataSource];
    }
    const originalHistoryLength = history.length;

    let columns = [];
    if (originalHistoryLength > 0) {
      columns = Object.keys(history[0]);

      if (originalHistoryLength > this.props.historyLengthLimit) {
        history = history.slice(0, this.props.historyLengthLimit);
      }
    }
    
    const style = { maxWidth:"97%", marginLeft:"1em", height:"20em" };
    return (
      <>
      <OwcTabs value={this.state.selectedDataSource} onValueChange={(ev) => this.handleTabChange(ev.detail)}>
        {this.state.sourceList.map((dataSource,index) => this.renderSourceTab(dataSource, index))}
      </OwcTabs>
      <OwcTable key={"DeviceDataSourcesTable" + this.props.deviceId} size='small' elevated
          cardProps={{ style: style }}>
        <OwcTableHeader sticky>
          {
            columns.map((columnName,index) => (
              <OwcTableHeaderCell key={"DeviceDataSourcesTable"+this.state.selectedDataSource+columnName} resizable>
                {(columnName in this.COLUMN_RENAME_DICT)?
                  this.COLUMN_RENAME_DICT[columnName]
                  :
                  convertCamelCaseToTitleCase(columnName)
                }
                </OwcTableHeaderCell>
            ))
          }
        </OwcTableHeader>
        <OwcTableBody loading={!this.state.historyPerSourceLoaded}>
          {history.map((row,index) => this.renderHistoryRow(row, index))}
        </OwcTableBody>
        <div slot="header" style={{ alignItems: 'center' }}>
          {
          <OwcTypography>
            {originalHistoryLength > this.props.historyLengthLimit
              ?(`Most Recent ${history.length} of ${originalHistoryLength>999?">":""}${originalHistoryLength} Recorded Lifecycle Event${originalHistoryLength===1?"":"s"}`)
              :(`${history.length} Recorded Lifecycle Event${history.length===1?"":"s"}`)
            }
          </OwcTypography>
          }
        </div>
      </OwcTable>
      </>
    );

  }
}

DeviceDataSources.defaultProps = {
  deviceId: null,
  forceUpdate: null,
  historyLengthLimit: 100,
}

export default DeviceDataSources
